<template>
  <div class="root">
    <div class="row" :class="state === 'found' && 'search-result'">
      <div class="col-12">
        <form id="emailSearch" novalidate @submit.prevent="search">
          <form-component class="form-row">
            <div v-if="state === 'found'" class="form__input">
              <label>User Email Address</label>
              <div class="selected">
                <span class="email">
                  <font-awesome-icon
                    aria-label="email address found"
                    class="icon alt"
                    :icon="['fas', 'check']"
                  />
                  {{ studentEmail }}
                </span>
                <button
                  title="remove selected and search again"
                  class="remove"
                  @click="state = 'initial'"
                >
                  <font-awesome-icon
                    class="icon alt"
                    :icon="['fas', 'times']"
                  />
                </button>
              </div>
            </div>

            <input-text
              v-if="state === 'initial' || state === 'notfound'"
              id="studentEmail"
              v-model="studentEmail"
              v-validate="'required|email'"
              data-vv-as="Email"
              :error="errors.first('studentEmail')"
              name="studentEmail"
              label="Student Email Address"
              type="email"
              placeholder="email@address.com"
              required
              :disabled="state === 'found'"
            />

            <button
              v-if="state !== 'found'"
              type="submit"
              class="dash-button"
              form="emailSearch"
              :disabled="searching"
              :aria-disabled="searching"
            >
              <font-awesome-icon
                v-if="searching"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Search</span>
            </button>
          </form-component>
        </form>
      </div>
    </div>
    <div v-if="state === 'found'" class="row adjustments">
      <div class="col-12">
        <form id="assignCredits" novalidate @submit.prevent="assign">
          <form-component class="form-row justify-start">
            <input-text
              id="studentCredits"
              v-model="studentCredits"
              v-validate="'required|integer|min_value:0'"
              data-vv-as="credits"
              :error="errors.first('studentCredits')"
              name="studentCredits"
              label="Number Of Credits"
              type="number"
              :min="0"
              required
              :disabled="state !== 'found'"
              class="smaller"
            />

            <button
              v-if="state === 'found'"
              class="dash-button"
              type="submit"
              form="assignCredits"
              :disabled="!dirty || submitting"
              :aria-disabled="!dirty || submitting"
            >
              <font-awesome-icon
                v-if="submitting"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Confirm</span>
            </button>
          </form-component>
        </form>
      </div>
    </div>

    <div
      id="helperText"
      class="collapse"
      :class="state === 'notfound' && 'show'"
      role="region"
      aria-live="polite"
    >
      <div v-if="state === 'notfound'" class="not-found">
        <font-awesome-icon
          class="icon alt"
          :icon="['fas', 'exclamation-circle']"
        />
        <div>
          <strong>Email Address Not Found</strong>
          <p>
            This email address is not currently registered with BetterMynd.
            Please try again or
            <a href="#" @click="emitInvite">Email Invitation</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminClient from '@/api/AdminClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';

export default {
  name: 'EditStudentAllowance',

  components: {
    FormComponent,
    InputText
  },

  props: {
    schoolId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      adminClient: new AdminClient(this.$http),

      studentEmail: '',
      studentCredits: '0',

      state: 'initial',
      currentCredits: null,
      searching: false,
      submitting: false
    };
  },

  computed: {
    dirty() {
      return Object.keys(this.veeValidateFields).some((key) => this.veeValidateFields[key].dirty);
    }
  },

  methods: {
    async search() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.searching = true;
      try {
        this.currentCredits =
          await this.adminClient.getStudentIndividualCreditsAvailable(
            this.schoolId,
            this.studentEmail
          );
        this.studentCredits = this.currentCredits.individualCredits.toString();
        this.state = 'found';
      } catch (e) {
        if (e.status === 400) {
          this.state = 'notfound';
        } else {
          this.$store.commit('setGlobalAlert', {
            msg: 'There was an error processing your request. Please try again!',
            error: true
          });
          this.hide();
        }
      }
      this.searching = false;
    },

    async assign() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.submitting = true;
      try {
        await this.adminClient.setStudentIndividualCreditAllowance(
          this.schoolId,
          this.currentCredits.studentUserId,
          parseInt(this.studentCredits, 10)
        );
        this.$store.commit('setGlobalAlert', {
          msg: "Successfully updated the user's available credits!"
        });
        this.state = 'initial';
        this.studentEmail = '';
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: "There was an error updating the user's credits. Please try again!",
          error: true
        });
      }
      this.submitting = false;
    },

    emitInvite() {
      this.$emit('invite', this.studentEmail);
      this.reset();
    },
    reset() {
      this.state = 'initial';
      this.studentEmail = '';
    }
  }
};
</script>

<style lang="scss" scoped>
.root {
  max-width: 575px;
  padding: 0 1.25rem;

  @media screen and (min-width: $sm-min) {
    padding: 0 2.45rem;
  }

  ::v-deep .form .form__input {
  label {
    text-transform: none;
    font-size: 1rem;
    letter-spacing: 0;
  }

  // &.form__input--error .form__error {
  //   margin-top: 4px;
  // }

  input {
    border-radius: 8px;
    border: 1px solid #d1d1d1;
    background: #fff;
    height: 46px;
    color: inherit;
    // margin-bottom: 0;

    &:focus {
      box-shadow: 0 2px 4px rgb(0 0 0 / 18%);
      border-color: #bababa;
    }

    &::placeholder {
      color: #000;
    }
  }
}
}

.dash-button {
  max-width: 90px;
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  justify-content: space-between;
  align-items: end;

  &.justify-start {
    justify-content: flex-start;
  }

  .form__input {
    flex: 1;
    margin-right: 16px;

    &.form__input--error + button {
      margin-bottom: 38px;
    }

    &.smaller {
      max-width: 160px;
    }
  }
}

.adjustments {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.selected {
  height: 45px;
  border: 1px solid #e8e8e8;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 1rem;
  padding: 0 5px 0 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .email {
    color: inherit;

    svg {
      color: #4cdb41;
      font-size: 1.25em;
      margin-right: 4px;
    }
  }
}

.remove {
  background: transparent;
  border-radius: 17px;
  border: 0;
  margin-bottom: 0;
  color: #c74141;
  height: 34px;
  width: 34px;
  font-size: 1.5em;
  line-height: 34px;
  opacity: 0.9;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover,
  &:focus {
    margin-bottom: 6px;
    opacity: 1;
  }

  &:active {
    color: #000;
    margin-bottom: 0;
  }
}

.not-found {
  margin-top: 16px;
  max-width: 480px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  strong {
    color: #ff4000;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 8px;
    display: block;
  }

  a {
    color: #2836b4;
    text-decoration: underline;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }

  p {
    font-size: 1rem;
  }

  svg {
    color: #ff4000;
    margin-right: 8px;
    font-size: 1.1rem;
  }
}
</style>
